/* eslint-disable react/style-prop-object */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedHTMLMessage, FormattedMessage, FormattedNumber } from 'react-intl';
import Field from 'redux-form/es/Field';
import AccordionTooltip from 'components/common/controls/AccordionTooltip';
import CheckboxInput from 'components/Credit24/Core/Controls/CheckboxInput';
import { fromCents, getCurrencyCode, isMobile } from 'utils/helpers';
import cx from 'classnames';

const propTypes = {
  upgrade: PropTypes.object,
  authentication: PropTypes.object,
};

const getTermsLink = (intl) => {
  const documentPath = intl.formatMessage({ id: 'offer.submit.confirm.terms.link' });
  return `${location.origin}${documentPath}`;
};

const getEuTermsLink = (intl, productId) => {
  const euDocumentLink = intl.formatMessage({ id: 'offer.submit.eu.terms.link' });
  return `${location.origin}${euDocumentLink}?productId=${productId}`;
};

const ConfirmationStep = ({ upgrade, authentication }, { intl }) => {
  const { address } = authentication;
  const { product } = upgrade;

  const creditorInfo = [
    { id: 'creditor.regNumber' },
    { id: 'creditor.address' },
    { id: 'creditor.tel' },
    { id: 'creditor.fax' },
    { id: 'creditor.email' },
  ];

  const customerInfo = [
    {
      id: 'customer.ssn',
      value: authentication.identifier,
    },
    {
      id: 'customer.address',
      value: `${address.street}, ${address.postcode}, ${address.city}`,
    },
    {
      id: 'customer.tel',
      value: authentication.msisdn,
    },
    {
      id: 'customer.email',
      value: authentication.email || authentication.emailPending,
    },
    {
      id: 'customer.bankAccount',
      value: authentication.bankAccount,
    },
  ];

  const financialInfo = [
    {
      id: 'newCreditLimit',
      value: fromCents(product.principal),
      style: 'currency',
    },
    {
      id: 'contractTerm',
      value: <FormattedMessage
        id="selfservice.creditInformation.contractTerm.value"
        values={{ term: product.maturityPeriods }}
      />,
    },
    {
      id: 'productType',
      value: <FormattedMessage id="selfservice.creditInformation.productType.creditLine" />,
    },
    {
      id: 'annualInterest',
      value: product.annualInterest,
      style: 'percent',
    },
    {
      id: 'apr',
      value: product.apr,
      style: 'percent',
      tooltip: (
        <AccordionTooltip
          title={<FormattedMessage id="selfservice.creditInformation.apr" />}
        >
          <div className="credit-info__item-tooltip">
            <FormattedMessage id="selfservice.creditInformation.apr.tooltip" />
          </div>
        </AccordionTooltip>
      ),
    },
    {
      id: 'withdrawalFixedFee',
      value: fromCents(product.invoicingFee),
      style: 'currency',
    },
    {
      id: 'drawFeePercentage',
      value: product.drawFeePercentage,
      style: 'percent',
    },
  ];

  const date = moment().format('DD.MM.YYYY');

  const legalListItemClasses = cx({
    'legal-info-list__item': true,
    'legal-info-list__item--main': !isMobile,
  });

  const creditorDetails = creditorInfo.map((item) => (
    <li className="legal-info-list__item" key={item.id}>
      <span className="legal-info-list__item-title">
        <FormattedMessage id={`legalinfo.${item.id}.label`} />
      </span>
      <span className="legal-info-list__item-value">
        <FormattedMessage id={`legalinfo.${item.id}.value`} />
      </span>
    </li>
  ));

  const customerDetails = customerInfo.map((item) => (
    <li className="legal-info-list__item" key={item.id}>
      <span className="legal-info-list__item-title">
        <FormattedMessage id={`legalinfo.${item.id}.label`} />
      </span>
      <span className="legal-info-list__item-value">
        {item.value}
      </span>
    </li>
  ));

  return (
    <div className="content-block">
      <div className="legal-info-section">
        <div className="legal-info-section__heading">
          <FormattedMessage id="legalinfo.title" values={{ date }} />
        </div>
        <div className="grid">
          <div className="grid__item one-half palm-one-whole">
            <ul className="legal-info-list">
              <li className={legalListItemClasses}>
                <span className="legal-info-list__item-title">
                  <FormattedMessage id="legalinfo.creditor.label" />
                </span>
                <span className="legal-info-list__item-value">
                  {isMobile ? (
                    <AccordionTooltip title={<FormattedMessage id="legalinfo.creditor.name" />} hideIcon>
                      <ul className="legal-info-list">
                        {creditorDetails}
                      </ul>
                    </AccordionTooltip>
                  ) : (
                    <FormattedMessage id="legalinfo.creditor.name" />
                  )}
                </span>
              </li>
              {!isMobile && creditorDetails}
            </ul>
          </div>
          <div className="grid__item one-half palm-one-whole">
            <ul className="legal-info-list">
              <li className={legalListItemClasses}>
                <span className="legal-info-list__item-title">
                  <FormattedMessage id="legalinfo.customer.label" />
                </span>
                <span className="legal-info-list__item-value">
                  {isMobile ? (
                    <AccordionTooltip
                      title={(
                        <span>
                          {authentication.firstName}
                          {' '}
                          {authentication.lastName}
                        </span>
)}
                      hideIcon
                    >
                      <ul className="legal-info-list">
                        {customerDetails}
                      </ul>
                    </AccordionTooltip>
                  ) : (
                    `${authentication.firstName} ${authentication.lastName}`
                  )}
                </span>
              </li>
              {!isMobile && customerDetails}
            </ul>
          </div>
        </div>
        <FormattedMessage id="selfservice.upgrade.financialInfoTitle" tagName="h3" />

        <table className="table table--financial-info">
          <tbody>
            {financialInfo.map((item) => (
              <tr key={item.id}>
                <td>
                  {item.tooltip || <FormattedMessage id={`selfservice.creditInformation.${item.id}`} />}
                </td>
                <td className="txt-right">
                  {item.style === 'percent'
                    && (
                    <FormattedNumber
                      style="percent"
                      minimumFractionDigits={2}
                      maximumFractionDigits={2}
                      value={item.value / 100}
                    />
                    )}
                  {item.style === 'currency'
                    && (
                    <FormattedNumber
                      style="currency"
                      currency={getCurrencyCode()}
                      value={item.value}
                    />
                    )}
                  {!item.style && item.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="legal-info-section__eu-terms">
          <a target="_blank" rel="noopener noreferrer" href={getEuTermsLink(intl, product.id)}>
            <FormattedMessage id="offer.submit.eu.terms" />
          </a>
        </p>
        <FormattedMessage id="selfservice.upgrade.generalClause" tagName="p" />
        <p>
          <Field
            name="submitAgree"
            component={CheckboxInput}
            text={(
              <FormattedHTMLMessage
                id="offer.submit.confirm.terms"
                values={{ termsLink: getTermsLink(intl) }}
              />
            )}
          />
        </p>
        {COUNTRY === 'FI' && !MOBILE_WALLET
          && (
          <p>
            <Field
              name="ASIAKASTIETO_DATAPOOL"
              component={CheckboxInput}
              text={<FormattedHTMLMessage id="signup.field.consent.ASIAKASTIETO_DATAPOOL.short" />}
              isErrorVisible
            />
          </p>
          )}
      </div>
    </div>
  );
};

ConfirmationStep.propTypes = propTypes;
ConfirmationStep.contextTypes = {
  intl: PropTypes.object,
};

export default ConfirmationStep;
